import * as React from "react";
import { Button, FlexRow, LoadingSpinner, MasonryGrid, Stack } from "atoms";
import { GalleryItemCard } from "./GalleryItemCard";
import { useGallery } from "../api/useGallery";
import type { DeleteItemFunction, GalleryItem } from "../types";

type GalleryItemsListProps = {
  artistId: string;
  galleryId: string;
  isEditing?: boolean;
  deleteItem?: DeleteItemFunction;
};

/** Component for displaying a gallery's items */
function GalleryItemsList({
  artistId,
  deleteItem = noop,
  galleryId,
  isEditing = false,
}: GalleryItemsListProps) {
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useGallery({
      artistIds: [artistId],
      galleryFolderIds: [galleryId],
      itemType: "item",
      perPage: 12,
    });

  const items = React.useMemo(
    () => data?.pages?.flatMap((page) => page.data) ?? [],
    [data]
  );

  if (isLoading || !items) {
    return <LoadingSpinner />;
  }

  const loadMoreGalleryItems = () => fetchNextPage();

  const createDeleteHandler =
    ({ id, itemType }: GalleryItem) =>
    () =>
      deleteItem({ itemId: id, itemType });

  return (
    <Stack gap="md">
      <MasonryGrid
        items={items}
        renderItem={(item) =>
          !item.image ? null : (
            <GalleryItemCard
              key={item.id}
              {...item}
              isEditing={isEditing}
              deleteItem={createDeleteHandler(item)}
            />
          )
        }
      />
      <FlexRow justifyContent="center" gap="20px">
        {hasNextPage && (
          <Button
            onClick={loadMoreGalleryItems}
            variant="primary"
            disabled={isFetchingNextPage}
          >
            Load More...
          </Button>
        )}
      </FlexRow>
    </Stack>
  );
}

export { GalleryItemsList };
